/**
 * Deterministic JSON serialisation, for values used as cache keys.
 *
 * `JSON.stringify` follows key INSERTION order, so two structurally identical
 * objects built by different code paths serialise differently. Anything that
 * derives an identity from a serialised object - a TanStack Query key, an
 * offline cache's view signature - needs the ordering removed, or it treats
 * equal things as different and silently misses its own cache.
 *
 * The contract, which callers depend on:
 * - object keys are sorted, recursively;
 * - `undefined` object VALUES are dropped, so an explicitly-undefined key is
 *   indistinguishable from an absent one (matching how callers build partial
 *   filter objects);
 * - `undefined` and `null` in ARRAY positions both serialise as `null`,
 *   because dropping them would shift the elements after them;
 * - array order is significant - it carries meaning that key order does not.
 */
export function stableStringify(value: unknown): string {
  if (value === null || value === undefined) {
    return 'null';
  }
  if (Array.isArray(value)) {
    // Array.from, NOT map: map SKIPS holes in a sparse array, leaving the
    // result sparse and joining to invalid JSON (`["a",,"b"]`). Array.from
    // visits holes as undefined, which the recursion renders as null.
    return `[${Array.from(value, (entry) => stableStringify(entry)).join(',')}]`;
  }
  if (typeof value === 'object') {
    const body = Object.entries(value as Record<string, unknown>)
      .filter(([, entryValue]) => entryValue !== undefined)
      .sort(([left], [right]) => compareKeys(left, right))
      .map(([key, entryValue]) => `${JSON.stringify(key)}:${stableStringify(entryValue)}`)
      .join(',');
    return `{${body}}`;
  }
  return JSON.stringify(value);
}

function compareKeys(left: string, right: string): number {
  if (left < right) {
    return -1;
  }
  return left > right ? 1 : 0;
}
